import { useCallback, useEffect, useState } from "react";
import PageShell from "../components/common/PageShell";
import { getMyBetsApi } from "../api/userApi";
import { inr, dt, errMsg } from "../utils/format";

const LIMIT = 20;
const kind = (b) => (b.netResult > 0 ? "w" : b.netResult < 0 ? "l" : "e");

export default function MyBets() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [f, setF] = useState("all");

  const load = useCallback(async (p) => {
    setLoading(true);
    setErr("");
    try {
      const res = await getMyBetsApi({ page: p, limit: LIMIT });
      const body = res.data || {};
      const list = Array.isArray(body.data) ? body.data : [];
      setRows((prev) => (p === 1 ? list : [...prev, ...list]));
      setPage(p);
      setTotal(body.pagination?.total ?? 0);
      setHasMore(!!body.pagination?.hasMore);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1); }, [load]);

  const shown = rows.filter((b) => f === "all" || (f === "won" ? b.netResult > 0 : b.netResult < 0));
  const staked = rows.reduce((s, b) => s + (b.totalBet || 0), 0);
  const won = rows.reduce((s, b) => s + (b.totalWin || 0), 0);
  const net = won - staked;

  return (
    <PageShell title="Bet History">
      <section className="pg-stats">
        <div className="pg-stat"><span className="pg-muted">Staked</span><b>{inr(staked)}</b></div>
        <div className="pg-stat"><span className="pg-muted">Won</span><b className="pg-win">{inr(won)}</b></div>
        <div className="pg-stat"><span className="pg-muted">Net</span><b className={net >= 0 ? "pg-win" : "pg-lose"}>{net >= 0 ? "+" : "-"}{inr(Math.abs(net))}</b></div>
      </section>
      {total > 0 && <p className="pg-muted">Showing {rows.length} of {total} rounds</p>}

      <div className="pg-tabs">
        {["all", "won", "lost"].map((t) => (
          <button key={t} className={`pg-tab ${f === t ? "on" : ""}`} onClick={() => setF(t)}>
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {err && <p className="pg-lose">{err}</p>}

      <section className="pg-card">
        {!loading && !err && shown.length === 0 ? (
          <p className="pg-empty">No bets here yet. Place a bet on the table and it will show up.</p>
        ) : (
          <table className="pg-table">
            <thead>
              <tr><th>Time</th><th>Round</th><th>Number</th><th>Bet</th><th>Win</th><th>Result</th><th>Balance</th></tr>
            </thead>
            <tbody>
              {shown.map((b) => (
                <tr key={b.roundId}>
                  <td data-l="Time">{dt(b.createdAt)}</td>
                  <td data-l="Round">{String(b.roundId).slice(-6)}</td>
                  <td data-l="Number"><span className={`ball sm ${b.winningColor}`}>{b.winningNumber}</span></td>
                  <td data-l="Bet">{inr(b.totalBet)}</td>
                  <td data-l="Win">{inr(b.totalWin)}</td>
                  <td data-l="Result">
                    <span className={`pg-tag ${kind(b)}`}>
                      {kind(b) === "w" ? `+${inr(b.netResult)}` : kind(b) === "l" ? `-${inr(Math.abs(b.netResult))}` : "Even"}
                    </span>
                  </td>
                  <td data-l="Balance">{inr(b.balanceAfter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {loading && <p className="pg-empty">Loading bets...</p>}
      </section>

      {hasMore && (
        <button className="pg-more" disabled={loading} onClick={() => load(page + 1)}>
          Load more
        </button>
      )}
    </PageShell>
  );
}
